//
// license.ts — fetch the embed's license info from our API at
// boot time. Everything the UI needs to decide what resolutions
// to offer (free tier → 720p only, paid → up to 4K) comes from
// here.
//
// Discovery of the API base URL:
//
//   The customer installs us via
//     <script src="https://mapvideoexport.com/embed.js" data-key="mve_..."></script>
//   We read both pieces from that script tag. The script's `src`
//   tells us the origin to call /api/embed/license on; the
//   `data-key` goes into the POST body. If no script tag with
//   data-key is present we fall back to the free tier without
//   touching the network.
//
// All failures (missing key, network errors, malformed response)
// resolve to the free tier. The embed should always boot to some
// usable state rather than breaking when our API is down.
//
import type { LicenseInfo } from '@mve/protocol';

const FREE_FALLBACK: LicenseInfo = {
  tier: 'free',
  allowHires: false,
  maxWidth: 720,
  watermark: true,
  creditsRemaining: 0,
};

//
// Locate the <script> tag that loaded us and read both the key
// (from data-key) and the API base URL (derived from the script's
// own src). Returns null for either when the tag isn't found in
// the expected shape — handled by the fetch path below.
//
function readEmbedConfig(): { key: string | null; apiBase: string | null } {
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src*="embed.js"]');
  for (const s of Array.from(scripts)) {
    const key = s.dataset.key ?? null;
    try {
      const src = new URL(s.src, window.location.href);
      return { key, apiBase: `${src.protocol}//${src.host}` };
    } catch {
      // Invalid src URL — skip.
    }
  }
  return { key: null, apiBase: null };
}

//
// Reserve a credit for an upcoming hires render. Called from the
// panel right before renderVideo when targetWidth exceeds the
// free-tier ceiling. Resolves to:
//
//   { ok: true,  renderId, creditsRemaining }   — proceed with render
//   { ok: false, reason }                        — abort, surface to user
//
// Network / parse failures resolve to { ok: false, reason: 'network' }
// so the caller can show a sensible message rather than crashing.
//
export interface ConsumeResult {
  ok: boolean;
  reason?: string;
  renderId?: string;
  creditsRemaining?: number;
}

export async function consumeCredit(args: {
  width: number;
  height: number;
  fps: number;
  durationSeconds: number;
  codec: string;
}): Promise<ConsumeResult> {
  const { key, apiBase } = readEmbedConfig();
  if (!apiBase) return { ok: false, reason: 'no_api_base' };
  try {
    const resp = await fetch(`${apiBase}/api/embed/consume`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        key,
        width: args.width,
        height: args.height,
        fps: args.fps,
        durationSeconds: args.durationSeconds,
        codec: args.codec,
        hostOrigin: window.location.origin,
      }),
    });
    if (resp.status === 402) {
      const body = (await resp.json().catch(() => ({}))) as { reason?: string };
      return { ok: false, reason: body.reason ?? 'insufficient_credits' };
    }
    if (!resp.ok) {
      return { ok: false, reason: 'server_error' };
    }
    const data = (await resp.json()) as ConsumeResult;
    return data;
  } catch {
    return { ok: false, reason: 'network' };
  }
}

//
// Ask the API for this key's gating. Network / parse failures
// silently degrade to the free-tier fallback; an embed must always
// be usable even if our license service is unreachable.
//
export async function fetchLicense(): Promise<LicenseInfo> {
  const { key, apiBase } = readEmbedConfig();
  if (!apiBase) return FREE_FALLBACK;
  try {
    const resp = await fetch(`${apiBase}/api/embed/license`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ key }),
    });
    if (!resp.ok) return FREE_FALLBACK;
    const data = (await resp.json()) as Partial<LicenseInfo>;
    // Defensive narrow: fill missing fields from the fallback so
    // the rest of the code can treat LicenseInfo as fully present.
    return {
      tier: data.tier === 'paid' ? 'paid' : 'free',
      allowHires: data.allowHires === true,
      maxWidth: typeof data.maxWidth === 'number' ? data.maxWidth : FREE_FALLBACK.maxWidth,
      watermark: data.watermark !== false,
      creditsRemaining: typeof data.creditsRemaining === 'number' ? data.creditsRemaining : 0,
    };
  } catch {
    return FREE_FALLBACK;
  }
}
